import { AbstractTemplateElementComponent } from './AbstractTemplateElementComponent';
import { tag } from '../../utils/tag';
import { hasTouchScreen } from '../../utils/_hasTouchScreen';
import { LifesComponent } from './LifesComponent';
import { PlaygroundComponent } from './PlaygroundComponent';
import { GameplayActionEvent, GameplayActionType } from '../../constants/GameplayActionEvent';
import { GameActionEvent, GameActionType } from '../../constants/GameActionEvent';
import { Directions } from '../../model/Snake';
import { SnakeCellsComponent } from './SnakeCellsComponent';
import { createElFromString } from '../../utils/elements/createElFromString';
import data from '../../../data/basic';
import logosSprite from '../../../assets/images/logos/logos.svg';
import YmSendGoal from '../../helpers/YmSendGoal';
import { YmEventGoals } from '../../constants/YmEventGoals';

const controls = {
    leftLabel: 'Лево',
    rightLabel: 'Право',
    upLabel: 'Наверх',
    downLabel: 'Вниз',
    pause: 'Пауза',
    resume: 'Продолжить',
    soundOff: 'Выключить музыку',
    soundOn: 'Включить музыку'
};

export class GameShellComponent extends AbstractTemplateElementComponent {
    #playgroundEl;
    #pauseEl;
    #toggleSoundEl;
    #scoreEl;
    #lifesEl;
    #lifesComp;
    #upEl;
    #downEl;
    #leftEl;
    #rightEl;
    #playgroundComp;
    #snakeComp;
    #isPlaying = true;
    #isSoundOn = false;
    #hasSounds = false;

    constructor(w, h, numLifes, hasSounds) {
        super();

        this.#hasSounds = hasSounds || false;

        this.#lifesComp = new LifesComponent(numLifes);
        this.#playgroundComp = new PlaygroundComponent(w, h);
        this.#snakeComp = new SnakeCellsComponent(w, h);

        this._needListeners = true;
    }

    _render(parentNode) {
        const rootEl = tag('section', 'game-shell');
        const infoEl = tag('footer', 'game-shell__info');
        const uiEl = tag('header', 'game-shell__ui');
        const playgroundEl = tag('div', 'game-shell__playground');
        const scoreEl = tag('h2', 'game-shell__score', '0');
        const lifesEl = tag('p', 'game-shell__lifes');
        const pauseEl = tag('button', 'game-button');
        const pauseInnerEl = tag('span', 'game-button__inner');
        const logoEl = tag('a', 'game-shell__logo-link');
        const logoElSvg = createElFromString(`
                            <svg width="92" height="45" viewBox="0 0 92 45">
                                <use xlink:href='${logosSprite}#logo-betcity-simple'></use>
                            </svg>
                        `);
        const coinsEl = createElFromString(`
                            <div class="game-shell__coins">
                                <div class="coin-icon coin-icon--animated"></div>
                                <div class="coin-icon coin-icon--animated"></div>
                            </div>
                        `);

        if (this.#hasSounds) this.renderSoundsControl(parentNode);

        pauseEl.classList.add('game-button--pause');
        pauseEl.classList.add('playing'); // Game is in progress / Pause is active
        pauseEl.setAttribute('aria-label', controls.pause);

        logoEl.setAttribute('href', data.betcityLink);
        logoEl.setAttribute('target', '_blank');
        logoEl.setAttribute('rel', 'noopener');
        logoEl.setAttribute('aria-label', data.betcityText);

        logoEl.appendChild(logoElSvg);

        scoreEl.classList.add('score');
        if (hasTouchScreen) rootEl.classList.add('game-shell--touch');

        infoEl.appendChild(scoreEl);
        infoEl.appendChild(lifesEl);
        infoEl.appendChild(coinsEl);

        pauseEl.appendChild(pauseInnerEl);
        uiEl.appendChild(pauseEl);

        rootEl.appendChild(uiEl);
        rootEl.appendChild(playgroundEl);
        rootEl.appendChild(infoEl);
        rootEl.appendChild(logoEl);

        // Controls for touch devices
        if (hasTouchScreen) {
            const controlsEl = tag('div', 'game-shell__controls');
            const arrowsWrapEl = tag('div', 'game-shell__arrows');
            const upEl = tag('button', 'game-arrow');
            const downEl = tag('button', 'game-arrow');
            const leftEl = tag('button', 'game-arrow');
            const rightEl = tag('button', 'game-arrow');
            const upEl2 = tag('span', 'game-arrow__inner');
            const downEl2 = tag('span', 'game-arrow__inner');
            const leftEl2 = tag('span', 'game-arrow__inner');
            const rightEl2 = tag('span', 'game-arrow__inner');

            upEl.classList.add('game-arrow--up');
            downEl.classList.add('game-arrow--down');
            leftEl.classList.add('game-arrow--left');
            rightEl.classList.add('game-arrow--right');
            arrowsWrapEl.classList.add('game-shell__arrows--separated');

            upEl.setAttribute('aria-label', controls.upLabel);
            downEl.setAttribute('aria-label', controls.downLabel);
            leftEl.setAttribute('aria-label', controls.leftLabel);
            rightEl.setAttribute('aria-label', controls.rightLabel);

            upEl.appendChild(upEl2);
            downEl.appendChild(downEl2);
            leftEl.appendChild(leftEl2);
            rightEl.appendChild(rightEl2);
            controlsEl.appendChild(arrowsWrapEl);
            arrowsWrapEl.appendChild(upEl);
            arrowsWrapEl.appendChild(downEl);
            arrowsWrapEl.appendChild(leftEl);
            arrowsWrapEl.appendChild(rightEl);
            rootEl.appendChild(controlsEl);

            this.#upEl = upEl;
            this.#downEl = downEl;
            this.#leftEl = leftEl;
            this.#rightEl = rightEl;
        }
        //

        this.#lifesComp.renderTo(lifesEl);
        this.#playgroundComp.renderTo(playgroundEl);
        this.#snakeComp.renderTo(playgroundEl);

        parentNode.appendChild(rootEl);

        this.#playgroundEl = playgroundEl;
        this.#pauseEl = pauseEl;
        this.#scoreEl = scoreEl;
        this.#lifesEl = lifesEl;

        return [rootEl];
    }

    renderSoundsControl(parentNode) {
        const toggleSoundEl = tag('button', 'game-button');

        toggleSoundEl.classList.add('game-button--toggle-sound');
        toggleSoundEl.setAttribute('aria-label', controls.soundOff);
        this.#toggleSoundEl = toggleSoundEl;
        parentNode.appendChild(toggleSoundEl);

        this.#toggleSoundEl.addEventListener('click', () => {
            this._toggleSoundHandler();
        });
    }

    _clear() {
        super._clear();

        this.#playgroundEl = null;
        this.#pauseEl = null;
        this.#toggleSoundEl = null;
        this.#scoreEl = null;
        this.#lifesEl = null;
        this.#upEl = null;
        this.#downEl = null;
        this.#leftEl = null;
        this.#rightEl = null;
        this.#lifesComp = null;
        this.#playgroundComp = null;
        this.#snakeComp = null;
    }

    _addListeners() {
        super._addListeners();

        this._keyboardHandler = (evt) => {
            // eslint-disable-next-line default-case
            switch (evt.code) {
                case 'ArrowRight': {
                    this.#dispatchControl(Directions.RIGHT);

                    break;
                }
                case 'ArrowLeft': {
                    this.#dispatchControl(Directions.LEFT);

                    break;
                }
                case 'ArrowUp': {
                    this.#dispatchControl(Directions.UP);

                    break;
                }
                case 'ArrowDown': {
                    this.#dispatchControl(Directions.DOWN);

                    break;
                }
                case 'Space': {
                    this.#dispatchPause();

                    break;
                }
            }
        };

        this._pauseHandler = () => {
            this.#dispatchPause();

            YmSendGoal(YmEventGoals.PAUSE_PLAY);
        };

        this._toggleSoundHandler = () => {
            this.isSoundOn = !this.#isSoundOn;
            this.#dispatchToggleSound();
        };

        this.#pauseEl.addEventListener('click', this._pauseHandler);

        if (hasTouchScreen) {
            this._upHandler = () => {
                this._keyboardHandler({ code: 'ArrowUp' });
            };
            this._downHandler = () => {
                this._keyboardHandler({ code: 'ArrowDown' });
            };
            this._leftHandler = () => {
                this._keyboardHandler({ code: 'ArrowLeft' });
            };
            this._rightHandler = () => {
                this._keyboardHandler({ code: 'ArrowRight' });
            };
            if (this.#upEl) this.#upEl.addEventListener('click', this._upHandler);
            if (this.#downEl) this.#downEl.addEventListener('click', this._downHandler);
            if (this.#leftEl) this.#leftEl.addEventListener('click', this._leftHandler);
            if (this.#rightEl) this.#rightEl.addEventListener('click', this._rightHandler);
        }

        window.addEventListener('keydown', this._keyboardHandler);
    }

    _removeListeners() {
        super._removeListeners();

        if (this._pauseHandler) {
            this.#pauseEl.removeEventListener('click', this._pauseHandler);
            this._pauseHandler = null;
        }

        if (this._toggleSoundHandler) {
            this.#toggleSoundEl?.removeEventListener('click', this._toggleSoundHandler);
            this._toggleSoundHandler = null;
        }

        if (this._upHandler && this.#upEl) {
            this.#upEl.addEventListener('click', this._upHandler);
            this._upHandler = null;
        }

        if (this._downHandler && this.#downEl) {
            this.#downEl.addEventListener('click', this._downHandler);
            this._downHandler = null;
        }

        if (this._leftHandler && this.#leftEl) {
            this.#leftEl.addEventListener('click', this._leftHandler);
            this._leftHandler = null;
        }

        if (this._rightHandler && this.#rightEl) {
            this.#rightEl.addEventListener('click', this._rightHandler);
            this._rightHandler = null;
        }

        if (typeof this._keyboardHandler === 'function') {
            window.removeEventListener('keydown', this._keyboardHandler);
            this._keyboardHandler = null;
        }
    }

    #dispatchControl(direction) {
        this.dispatchEvent({
            type: GameplayActionEvent,
            action: {
                type: GameplayActionType.DIRECTION,
                direction
            }
        });
    }

    #dispatchPause() {
        this.dispatchEvent({
            type: GameplayActionEvent,
            action: {
                type: GameplayActionType.PAUSE
            }
        });
    }

    #dispatchToggleSound() {
        this.dispatchEvent({
            type: GameActionEvent,
            action: {
                type: GameActionType.TOGGLESOUND
            }
        });
    }

    setSize(w, h) {
        this.#playgroundComp.remove(true, true);
        this.#snakeComp.remove(true, true);

        this.#playgroundComp.setSize(w, h);
        this.#snakeComp.setSize(w, h);

        this.#playgroundComp.renderTo(this.#playgroundEl);
        this.#snakeComp.renderTo(this.#playgroundEl);

        this.#playgroundComp.show();
        this.#snakeComp.show();
    }

    get playgroundComp() {
        return this.#playgroundComp;
    }

    get snakeComp() {
        return this.#snakeComp;
    }

    set isPlaying(isPlaying) {
        if (isPlaying === this.#isPlaying) return;

        if (isPlaying) {
            this.#pauseEl.classList.add('playing');
            this.#pauseEl.setAttribute('aria-label', controls.pause);
        } else {
            this.#pauseEl.classList.remove('playing');
            this.#pauseEl.setAttribute('aria-label', controls.resume);
        }

        this.#isPlaying = isPlaying;
    }

    get isPlaying() {
        return this.#isPlaying;
    }

    set isSoundOn(isSoundOn) {
        if (!this.#toggleSoundEl || isSoundOn === this.#isSoundOn) return;

        if (isSoundOn) {
            this.#toggleSoundEl.classList.add('sound-on');
            this.#toggleSoundEl.setAttribute('aria-label', controls.soundOff);
        } else {
            this.#toggleSoundEl.classList.remove('sound-on');
            this.#toggleSoundEl.setAttribute('aria-label', controls.soundOn);
        }

        this.#isSoundOn = isSoundOn;
    }

    set score(value) {
        this.#scoreEl.innerText = value.toString();
    }

    get score() {
        return this.#scoreEl.innerText;
    }

    set lifesViewType(value) {
        this.#lifesComp.viewType = value;
    }

    get lifesViewType() {
        return this.#lifesComp.viewType;
    }

    set lifes(value) {
        this.#lifesComp.value = value;
    }

    get lifes() {
        return this.#lifesComp.value;
    }

    get size() {
        return this.#snakeComp.size;
    }

    getSyncBoundingRect() {
        return this.#playgroundEl ? this.#playgroundEl.getBoundingClientRect() : null;
    }
}
